import React from 'react';
import { cn } from '../../lib/utils';

export interface TailwindButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  icon?: string; // Material Symbols icon name
  iconPosition?: 'left' | 'right';
  children?: React.ReactNode;
  fullWidth?: boolean;
}

export default function TailwindButton({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  children,
  fullWidth = false,
  className,
  disabled,
  ...props
}: TailwindButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantClasses = {
    primary: 'bg-primary hover:bg-blue-700 text-white shadow-md shadow-primary/20 focus:ring-primary disabled:bg-primary/50',
    secondary: 'bg-white border border-slate-200 hover:border-primary text-slate-700 hover:text-primary shadow-sm focus:ring-primary disabled:opacity-50',
    ghost: 'bg-transparent hover:bg-slate-100 text-slate-600 hover:text-slate-900 focus:ring-slate-300 disabled:opacity-50',
    danger: 'bg-red-600 hover:bg-red-700 text-white shadow-md shadow-red-600/20 focus:ring-red-600 disabled:bg-red-600/50',
    dark: 'bg-slate-900 hover:bg-slate-800 text-white shadow-md shadow-slate-900/20 focus:ring-slate-700 disabled:bg-slate-400',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-base',
  };

  const iconSizeClasses = {
    sm: 'text-[16px]',
    md: 'text-[18px]',
    lg: 'text-[20px]',
  };

  const disabledClasses = disabled ? 'cursor-not-allowed' : 'cursor-pointer';
  const widthClasses = fullWidth ? 'w-full' : '';

  return (
    <button
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        disabledClasses,
        widthClasses,
        className
      )}
      disabled={disabled}
      {...props}
    >
      {icon && iconPosition === 'left' && (
        <span className={cn('material-symbols-outlined', iconSizeClasses[size])}>
          {icon}
        </span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className={cn('material-symbols-outlined', iconSizeClasses[size])}>
          {icon}
        </span>
      )}
    </button>
  );
}
